import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, GripVertical, Trash2, ArrowLeft, Save } from 'lucide-react'
import { formsApi, categoriesApi, masterDataApi, workflowsApi } from '@/lib/api'

const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Numero' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Selecao' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'photo', label: 'Foto' },
  { value: 'barcode', label: 'Codigo de barras' },
  { value: 'signature', label: 'Assinatura' },
  { value: 'boolean', label: 'Sim/Nao' },
]

interface LocalField {
  uid: string
  id?: number
  field_name: string
  field_label: string
  field_type: string
  is_required: boolean
  sort_order: number
  data_source?: string      // master-data source for select fields
  options?: string          // comma-separated options for manual selects
  visibility_rule?: string  // JSON: {"field":"...","operator":"...","value":"..."}
}

function toSnakeCase(label: string) {
  return label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
}

function SortableField({ field, onChange, onDelete, mdLists, allFields }: {
  field: LocalField
  onChange: (uid: string, update: Partial<LocalField>) => void
  onDelete: (uid: string) => void
  mdLists: { id: number; slug: string; name: string; is_active: boolean }[]
  allFields: LocalField[]
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.uid })
  const style = { transform: CSS.Transform.toString(transform), transition }

  // Parse current visibility rule
  const rule = (() => {
    try { return field.visibility_rule ? JSON.parse(field.visibility_rule) : null }
    catch { return null }
  })()
  const ruleEnabled = rule !== null
  const triggerFields = allFields.filter(f => f.uid !== field.uid && f.field_name)

  function setRule(patch: Record<string, string> | null) {
    if (patch === null) { onChange(field.uid, { visibility_rule: '' }); return }
    const current = rule ?? { field: '', operator: 'equals', value: '' }
    onChange(field.uid, { visibility_rule: JSON.stringify({ ...current, ...patch }) })
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-3 rounded-lg border bg-white p-3">
      <button {...attributes} {...listeners} className="mt-2 cursor-grab text-gray-300 hover:text-gray-500">
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 grid grid-cols-3 gap-2">
        <input className="input text-sm col-span-2" placeholder="Label do campo"
          value={field.field_label}
          onChange={e => {
            const label = e.target.value
            onChange(field.uid, {
              field_label: label,
              field_name: field.field_name || toSnakeCase(label),
            })
          }} />
        <select className="input text-sm" value={field.field_type}
          onChange={e => onChange(field.uid, { field_type: e.target.value, data_source: '' })}>
          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {field.field_type === 'select' && (
          <div className="col-span-3 space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">Origem dos dados:</label>
              <select
                className="input text-xs flex-1"
                value={field.data_source ?? ''}
                onChange={e => onChange(field.uid, { data_source: e.target.value })}>
                <option value="">Manual (lista fixa)</option>
                <option value="categories">Categorias</option>
                <option value="departments">Departamentos</option>
                {mdLists.filter(l => l.is_active).map(l => (
                  <option key={l.slug} value={l.slug}>{l.name}</option>
                ))}
              </select>
            </div>
            {(!field.data_source) && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  Opções <span className="text-gray-400">(uma por linha)</span>
                </label>
                <textarea
                  className="input text-xs w-full font-mono"
                  rows={4}
                  placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                  value={(field.options ?? '').replace(/,/g, '\n')}
                  onChange={e => onChange(field.uid, {
                    // Preserve newlines as commas during typing (do NOT filter here —
                    // filtering strips trailing newlines and prevents adding new lines)
                    options: e.target.value.split('\n').join(',')
                  })}
                />
              </div>
            )}
          </div>
        )}
        <label className="col-span-3 flex items-center gap-2 text-xs text-gray-500">
          <input type="checkbox" checked={field.is_required}
            onChange={e => onChange(field.uid, { is_required: e.target.checked })} />
          Obrigatorio
          <span className="ml-2 text-gray-400 font-mono">{field.field_name || '-'}</span>
        </label>

        {/* Visibility rule */}
        <div className="col-span-3 border-t pt-2">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
            <input type="checkbox" checked={ruleEnabled}
              onChange={e => {
                if (e.target.checked) {
                  onChange(field.uid, { visibility_rule: JSON.stringify({ field: '', operator: 'equals', value: '' }) })
                } else {
                  onChange(field.uid, { visibility_rule: '' })
                }
              }} />
            <span className="font-medium text-gray-600">Condição de visibilidade</span>
          </label>
          {ruleEnabled && (
            <div className="mt-2 flex flex-wrap gap-2 items-center pl-4">
              <span className="text-xs text-gray-400">Mostrar quando</span>
              <select className="input text-xs" value={rule?.field ?? ''}
                onChange={e => setRule({ field: e.target.value })}>
                <option value="">-- campo --</option>
                {triggerFields.map(f => (
                  <option key={f.uid} value={f.field_name}>{f.field_label || f.field_name}</option>
                ))}
              </select>
              <select className="input text-xs" value={rule?.operator ?? 'equals'}
                onChange={e => setRule({ operator: e.target.value })}>
                <option value="equals">é igual a</option>
                <option value="not_equals">é diferente de</option>
                <option value="is_filled">está preenchido</option>
                <option value="is_empty">está vazio</option>
              </select>
              {(rule?.operator === 'equals' || rule?.operator === 'not_equals') && (
                <input className="input text-xs w-32" placeholder="valor"
                  value={rule?.value ?? ''}
                  onChange={e => setRule({ value: e.target.value })} />
              )}
            </div>
          )}
        </div>
      </div>
      <button className="mt-1 rounded p-1 text-red-500 hover:bg-red-50" onClick={() => onDelete(field.uid)}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}

export default function FormBuilderPage() {
  const { id } = useParams<{ id?: string }>()
  const isEditing = Boolean(id && id !== 'new')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [meta, setMeta] = useState({ name: '', description: '', category: '', is_active: true, workflow_id: null as number | null })
  const [fields, setFields] = useState<LocalField[]>([])
  const [error, setError] = useState<string | null>(null)

  const { data: catsData } = useQuery({
    queryKey: ['form-categories'],
    queryFn: () => categoriesApi.list(),
    staleTime: 60_000,
  })
  const categories = (catsData?.data ?? []) as { id: number; name: string }[]

  const { data: mdListsData } = useQuery({
    queryKey: ['master-data-lists'],
    queryFn: () => masterDataApi.getLists(),
    staleTime: 60_000,
  })
  const mdLists = (mdListsData?.data ?? []) as { id: number; slug: string; name: string; is_active: boolean }[]

  const { data: workflowsData } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => workflowsApi.list(),
    staleTime: 60_000,
  })
  const workflows = (workflowsData?.data ?? []) as import('@/types').WorkflowDefinition[]

  const { data: existingForm, isLoading, isError: formNotFound } = useQuery({
    queryKey: ['form', id],
    queryFn: () => formsApi.get(Number(id)),
    enabled: isEditing,
    retry: false,
  })

  useEffect(() => {
    if (existingForm?.data) {
      const f = existingForm.data as any
      setMeta({ name: f.name, description: f.description ?? '', category: f.category ?? '', is_active: f.is_active, workflow_id: f.workflow_id ?? null })
      setFields((f.fields ?? []).map((fld: any, i: number) => ({
        uid: `existing-${fld.id ?? i}`,
        id: fld.id,
        field_name: fld.field_name,
        field_label: fld.field_label,
        field_type: fld.field_type,
        is_required: fld.is_required,
        sort_order: fld.sort_order,
        data_source: fld.data_source ?? '',
        options: fld.options ?? '',
        visibility_rule: fld.visibility_rule ?? '',
      })))
    }
  }, [existingForm])

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        name: meta.name,
        description: meta.description,
        category: meta.category,
        is_active: meta.is_active,
        workflow_id: meta.workflow_id,
        fields: fields.map((f, i) => ({
          field_name: f.field_name || toSnakeCase(f.field_label || `campo_${i}`),
          field_label: f.field_label || `Campo ${i + 1}`,
          field_type: f.field_type,
          is_required: f.is_required,
          sort_order: i,
          data_source: f.data_source || null,
          options: (!f.data_source && f.options)
            ? f.options.split(',').map((s: string) => s.trim()).filter(Boolean).join(',') || null
            : null,
          visibility_rule: f.visibility_rule || null,
        })),
      }
      if (isEditing) {
        return formsApi.update(Number(id), payload as any)
      }
      return formsApi.create(payload as any)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] })
      queryClient.invalidateQueries({ queryKey: ['form', id] })
      navigate('/forms')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      const message = Array.isArray(detail)
        ? detail.map((e: any) => e.msg ?? JSON.stringify(e)).join('; ')
        : detail ?? err?.response?.data?.error ?? 'Erro ao guardar'
      setError(message)
    },
  })

      const addField = (type: string) => {
    setFields(prev => [...prev, {
      uid: `new-${Date.now()}`,
      field_name: '',
      field_label: '',
      field_type: type,
      is_required: false,
      sort_order: prev.length,
      data_source: '',
      options: '',
      visibility_rule: '',
    }])
  }

  const updateField = (uid: string, update: Partial<LocalField>) => {
    setFields(prev => prev.map(f => f.uid === uid ? { ...f, ...update } : f))
  }

  const deleteField = (uid: string) => {
    setFields(prev => prev.filter(f => f.uid !== uid))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setFields(items => {
        const oldIndex = items.findIndex(i => i.uid === active.id)
        const newIndex = items.findIndex(i => i.uid === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    )
  }

  if (isEditing && formNotFound) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-lg font-medium text-gray-700">Formulário não encontrado</p>
        <p className="text-sm text-gray-500">O formulário com ID {id} não existe ou não tem acesso.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/forms')}>Voltar à lista</button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/forms')} className="rounded p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Formulario' : 'Novo Formulario'}
        </h1>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4">
          <div className="card space-y-4">
            <h3 className="font-semibold text-gray-900">Propriedades</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input className="input w-full" placeholder="Nome do formulario"
                value={meta.name} onChange={e => setMeta(m => ({ ...m, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              <select className="input w-full" value={meta.category}
                onChange={e => setMeta(m => ({ ...m, category: e.target.value }))}>
                <option value="">Sem categoria</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workflow de aprovação</label>
              <select className="input w-full" value={meta.workflow_id ?? ''}
                onChange={e => setMeta(m => ({ ...m, workflow_id: e.target.value ? Number(e.target.value) : null }))}>
                <option value="">Sem workflow</option>
                {workflows.filter(w => w.is_active).map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              {meta.workflow_id && (
                <p className="mt-1 text-xs text-green-600">✓ Workflow ativo — submissões irão iniciar automaticamente</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
              <textarea className="input w-full resize-none" rows={2} placeholder="Opcional..."
                value={meta.description} onChange={e => setMeta(m => ({ ...m, description: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={meta.is_active}
                onChange={e => setMeta(m => ({ ...m, is_active: e.target.checked }))} />
              Formulario ativo
            </label>
          </div>

          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3">Adicionar campo</h3>
            <div className="grid grid-cols-2 gap-2">
              {FIELD_TYPES.map(t => (
                <button key={t.value} className="btn btn-secondary text-xs py-1.5 flex items-center gap-1"
                  onClick={() => addField(t.value)}>
                  <Plus className="h-3 w-3" /> {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Campos ({fields.length})</h3>
          </div>

          {fields.length === 0 ? (
            <div className="py-12 text-center text-gray-400 border-2 border-dashed rounded-lg">
              <p>Adiciona campos usando o painel a esquerda</p>
            </div>
          ) : (
            <div className="space-y-2">
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fields.map(f => f.uid)} strategy={verticalListSortingStrategy}>
                  {fields.map(field => (
                    <SortableField key={field.uid} field={field}
                      onChange={updateField} onDelete={deleteField} mdLists={mdLists} allFields={fields} />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3 pt-4 border-t">
            <button className="btn btn-secondary" onClick={() => navigate('/forms')}>Cancelar</button>
            <button className="btn btn-primary flex items-center gap-2"
              disabled={!meta.name || saveMut.isPending}
              onClick={() => saveMut.mutate()}>
              <Save className="h-4 w-4" />
              {saveMut.isPending ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}